import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spin, Tooltip, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { findIndex } from 'lodash';

import Button from "../../../../../../../components/uiComponent/button/index";

import ViewEye from '../../../../../../../assets/icons/view-eye.svg';
import OverWrite from '../../../../../../../assets/icons/over-write.svg';
import PrintIcon from '../../../../../../../assets/icons/print-icon.svg';
import Edit from '../../../../../../../assets/icons/edit-line.svg';
import Delete from '../../../../../../../assets/icons/remove-icon.svg';
import WarningIcon from '../../../../../../../assets/icons/warning-modal-icon.svg';
import Modal from "../../../../../../../components/uiComponent/modal"

import getBoxIndex from '../../../../../../../utils/get-box-index';

import { AccordionCardsDataWrapper, DeleteModalStyleWrapper } from '../../style'

import EditBoxDimensionsModal from './EditBoxDimensionsModal';
import BoxDetailsInfoDrawer from './boxDetailInfo';

import { ClearBoxItems, GetWarehouseShipments, DeleteBox, GetPackingItems } from '../../../../../../../slices/post-shipment';
import { GetOrderItems } from '../../../../../../../slices/work-orders';
import mix2DLablePdf from '../../../../../../../actions/utils/mix2DLablePdf';
import box2DLabels from '../../../../../../../utils/print-labels/box2DLabels';
import { buildPackNowLabelItems, formatBoxCode, isValidFnsku, notifyMissingFnsku } from './packNowLabelHelpers';

const AccordionCardsData = ({
  boxesList,
  setCreateMultiSKU,
  itemShipmentIndex,
  activeShipmentIndex,
  setDefaultTab,
  filteredBoxesList,
  boxCurrentPage = 1,
  setBoxCurrentPage
}) => {
  const itemsPerPage = 20;
  const dispatch = useDispatch();
  const { workOrderId } = useParams();

  const [boxIndex, setBoxIndex] = useState(-1);
  const [editBox, setEditBox] = useState(false);
  const [isDeleteBox, setIsDeleteBox] = useState(false);
  const [isEmptyBox, setIsEmptyBox] = useState(false);
  const [showBoxItems, setShowBoxItems] = useState(false);
  const [boxDeletingLoading, setBoxDeletingLoading] = useState(false);
  const [displayedBoxesList, setDisplayedBoxesList] = useState([]);
  const [loadedBoxes, setLoadedBoxes] = useState(itemsPerPage);
  const [hasMoreBoxes, setHasMoreBoxes] = useState(true);
  const boxScrollContainerRef = useRef(null);

  const {
    shipmentDetails,
    settingPackInformation,
    clearingBoxItems,
    packingMethod,
    packingItems,
    packingGroups,
    boxWarningMap,
  } = useSelector((state) => state.postShipment || {});

  const firstGroup = packingGroups?.[0];
  const packingGroupId = typeof firstGroup === 'string' ? firstGroup : firstGroup?.packingGroupId || null;

  // Handle infinite scroll for boxes
  useEffect(() => {
    if (!filteredBoxesList?.length) {
      setDisplayedBoxesList([]);
      setLoadedBoxes(itemsPerPage);
      setHasMoreBoxes(false);
      return;
    }

    setLoadedBoxes(itemsPerPage);
    setHasMoreBoxes(filteredBoxesList.length > itemsPerPage);
  }, [filteredBoxesList]);

  // Handle infinite scroll data loading for boxes
  useEffect(() => {
    if (!filteredBoxesList?.length) {
      setDisplayedBoxesList([]);
      return;
    }

    const slicedData = filteredBoxesList.slice(0, loadedBoxes);
    setDisplayedBoxesList(slicedData);
    setHasMoreBoxes(loadedBoxes < filteredBoxesList.length);
  }, [filteredBoxesList, loadedBoxes]);

  // Infinite scroll handler for boxes
  const handleBoxScroll = useCallback(() => {
    if (!boxScrollContainerRef.current || !hasMoreBoxes) return;

    const { scrollTop, scrollHeight, clientHeight } = boxScrollContainerRef.current;
    
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      const nextLoadedBoxes = Math.min(loadedBoxes + itemsPerPage, filteredBoxesList.length);
      setLoadedBoxes(nextLoadedBoxes);
    }
  }, [hasMoreBoxes, loadedBoxes, filteredBoxesList.length]);

  // Add scroll event listener for boxes
  useEffect(() => {
    const scrollContainer = boxScrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleBoxScroll);
      return () => scrollContainer.removeEventListener('scroll', handleBoxScroll);
    }
  }, [handleBoxScroll]);


  const handleEmptyBox = () => {
    const isPackNow = packingMethod === 'pack_now';
    dispatch(ClearBoxItems(filteredBoxesList[boxIndex]?.id))
      .then(({ payload }) => {
        if (payload?.success) {
          setIsEmptyBox(false);
          dispatch(GetWarehouseShipments({ workOrderId }));
          dispatch(GetPackingItems({ workOrderId }));
          dispatch(GetOrderItems({ workOrderId, page: { pageSize: 1000, pageNumber: 1 } }));
        }
      });
  };

  const handleDeleteBox = () => {
    const isPackNow = packingMethod === 'pack_now';
    setBoxDeletingLoading(true);
    dispatch(DeleteBox(filteredBoxesList[boxIndex]?.id))
      .then(() => {
        setDefaultTab();
        setIsDeleteBox(false);
        setBoxDeletingLoading(false);
        dispatch(GetWarehouseShipments({ workOrderId }));
        dispatch(GetPackingItems({ workOrderId }));
        dispatch(GetOrderItems({ workOrderId, page: { pageSize: 1000, pageNumber: 1 } }));
      });
  };
  const handleGenerateMix2DBoxLabel = (index, shipments, boxId) => {
    const isPackNow = packingMethod === 'pack_now';

    // Pack Now does not have shipment details yet, so print only the box contents label.
    if (isPackNow) {
      const box = boxesList?.find(({ id }) => id === boxId) || {};
      const { items = [], box_code, weight, length, height, width, unit, created_at } = box;
      const mappedItems = buildPackNowLabelItems({ boxItems: items, packingItems });
      const enrichedItems = mappedItems.map((mi) => {
        const packingItem = packingItems?.find(pi => pi.fnsku === mi.fnsku || pi.id === mi.sku_id) || {};
        return { ...mi, name: packingItem.item_name || packingItem.name || '', msku: packingItem.msku || packingItem.sku || '' };
      });

      const missingFnsku = enrichedItems.filter(mi => !isValidFnsku(mi.fnsku)).map(mi => mi.msku || '—');
      if (missingFnsku.length) {
        notifyMissingFnsku(missingFnsku.join(', '));
        return;
      }

      box2DLabels({
        isPackNow: true,
        boxCode: formatBoxCode(box_code, box.id),
        boxNumber: index + 1,
        items: enrichedItems,
        dimensions: { length, width, height, unit },
        weight,
        createdAt: created_at,
      });
      return;
    }

    // Pack Later: existing Amazon FBA label flow
    const shipment = shipments?.find(({ boxes_info }) =>
      boxes_info?.some(({ id }) => id === boxId)
    );
    if (!shipment) return;

    const {
      shipment_items_info = [],
      amazon_shipment_confirmation_id: shipmentId = '',
      name: shipmentName = 'Manual Packing',
      amazon_shipment_id: amazonShipmentId = '',
      amazon_warehouse_id: FCID = '',
      destination_name = '',
      destination_city = '',
      destination_postal_code = '',
      destination_address_line_1 = '',
      destination_state_or_province_code = '',
      source_city = '',
      source_country_code = '',
      source_postal_code = '',
      source_name = '',
      source_address_line_1 = '',
      boxes_info = []
    } = shipment;

    const { items = [], type = '' } = boxes_info?.find(({ id }) => id === boxId) || {};

    const mappedItems = items?.map(({ boxed_quantity, shipment_item_id }) => {
      const item = shipment_items_info.find(i => i.id === shipment_item_id) || {};
      const { fnsku, expiration, msku } = item;
      let exp = null;
      if (expiration) exp = moment(expiration, "YYYY-MM-DD").format("YYMMDD");
      return { fnsku, qty: boxed_quantity, exp, msku };
    }) || [];

    const missingFnsku = mappedItems.filter(mi => !isValidFnsku(mi.fnsku)).map(mi => mi.msku || '—');
    if (missingFnsku.length) {
      notifyMissingFnsku(missingFnsku.join(', '));
      return;
    }

    const ship = {
      warehouse_name: source_name,
      address_line1: source_address_line_1,
      city: source_city,
      state_or_province_code: source_postal_code,
      country_code: source_country_code,
      ship_to_name: destination_name,
      destination_fulfillment_center_id: FCID,
      ship_to_address1: destination_address_line_1,
      ship_to_city: destination_city,
      ship_to_state: destination_state_or_province_code,
      ship_to_postal_code: destination_postal_code
    };

    mix2DLablePdf({
      shipmentId,
      userName: 'Opulent',
      shipmentName,
      dateCreated: moment().format("DD/MM/YY"),
      outbound_shipment_id: amazonShipmentId,
      items: mappedItems,
      numberOfBox: 1,
      title: (mappedItems?.length > 1 || type === 'multi-sku') ? '' : (shipment_items_info.find(({ id }) => id === items?.[0]?.shipment_item_id)?.item_name || ''),
      PageStartIndex: 0,
      is2D: (mappedItems?.length > 1 || type === 'multi-sku') ? false : true,
      ship,
      boxIndex: index + 1,
    });
  };

  return (
    <AccordionCardsDataWrapper>
      {
        (activeShipmentIndex !== -1 || packingMethod === 'pack_now') &&
        <Button onClick={() => setCreateMultiSKU(true)} className="outlined" text="Create Multi SKU Box" />
      }
      <Spin tip='Loading...' spinning={settingPackInformation}>
        <div 
          className='cards-wrapper'
          ref={boxScrollContainerRef}
          style={{
            maxHeight: '100%',
            overflowY: 'auto',
            paddingRight: '8px'
          }}
        >
          {displayedBoxesList?.map?.((item, index) => {
            const isPackNow = packingMethod === 'pack_now';
            const boxNum = isPackNow
              ? (filteredBoxesList.findIndex(b => b.id === item.id) + 1)
              : (getBoxIndex(filteredBoxesList, item?.id, shipmentDetails?.shipments) + 1);
            const shipmentConfirmId = isPackNow
              ? ''
              : (shipmentDetails?.shipments?.find(({ boxes_info }) => boxes_info?.some(({ id }) => id === item.id))?.amazon_shipment_confirmation_id || '');
            const isMultiSku = item.type === 'multi-sku' || Number(item.mnsku) > 1;
            const singleSkuFnsku = !isMultiSku
              ? (packingItems?.find(pi => pi.sku_id === item.items?.[0]?.sku_id)?.fnsku || '')
              : '';

            return (
            <div key={filteredBoxesList?.length + index} className='cards-items'>
              <div className='title'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <p>{`Box ${boxNum}${shipmentConfirmId ? ` / ${shipmentConfirmId}` : ''}`}</p>
                  {(boxWarningMap?.[String(item.id)] || []).map((err, i) => (
                    <Tooltip key={i} title={err.message}>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '1px 7px',
                        borderRadius: '10px',
                        background: err.severity === 'ERROR' ? '#FFEBEE' : '#FFF3E0',
                        color: err.severity === 'ERROR' ? '#C62828' : '#E65100',
                        border: `1px solid ${err.severity === 'ERROR' ? '#EF9A9A' : '#FFCC80'}`,
                        whiteSpace: 'nowrap',
                        cursor: 'pointer'
                      }}>
                        {err.severity === 'ERROR' ? '🚫 Error' : '⚠️ Warning'}
                      </span>
                    </Tooltip>
                  ))}
                  {!isMultiSku && singleSkuFnsku && (
                    <Tooltip title={singleSkuFnsku}>
                      <span style={{ fontSize: '11px', color: '#4E5969', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {singleSkuFnsku}
                      </span>
                    </Tooltip>
                  )}
                </div>
                <div className='actions'>
                  <Tooltip title="Print Label">
                    <img
                      src={PrintIcon}
                      alt="PrintIcon"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleGenerateMix2DBoxLabel(boxNum - 1, shipmentDetails?.shipments, item?.id)}
                    />
                  </Tooltip>
                  <Tooltip title='View'>
                    <img
                      onClick={() => {
                        const actualIndex = filteredBoxesList.findIndex(box => box.id === item.id);
                        setBoxIndex(actualIndex);
                        setShowBoxItems(true);
                      }}
                      src={ViewEye}
                      alt="ViewEye"
                    />
                  </Tooltip>
                  <Tooltip title={isPackNow ? 'Delete' : 'Clear'}>
                    <img
                      onClick={() => {
                        const actualIndex = filteredBoxesList.findIndex(box => box.id === item.id);
                        setBoxIndex(actualIndex);
                        if (isPackNow) {
                          setIsDeleteBox(true);
                        } else {
                          setIsEmptyBox(true);
                        }
                      }}
                      src={Delete}
                      alt="Delete"
                      style={{ cursor: 'pointer' }}
                    />
                  </Tooltip>
                </div>
              </div>
              <div className='title-items'>
                <div className='card-items'>
                  <p>MNSKU:</p>
                  <p>{item.mnsku || 0}</p>
                </div>
                <div className='card-items'>
                  <p>Units:</p>
                  <p>{item.units || 0}</p>
                </div>
                <div className='card-items'>
                  <p>Dimension:</p>
                  <div className='items-action-wrapper'>
                    <p>{`${parseFloat(item.length || 0)} x ${parseFloat(item.width || 0)} x ${parseFloat(item.height || 0)} x ${parseFloat(item.weight || 0)} ${item.unit || ''}`}</p>
                    <img onClick={() => {
                      const actualIndex = filteredBoxesList.findIndex(box => box.id === item.id);
                      setEditBox(true);
                      setBoxIndex(actualIndex);
                    }} src={Edit} alt="Edit" />
                  </div>
                </div>
              </div>
            </div>
          )})}
          {hasMoreBoxes && displayedBoxesList.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '20px',
              color: '#666'
            }}>
              <span>Scroll down to load more boxes...</span>
            </div>
          )}
          {
            filteredBoxesList?.length === 0 &&
            <div className='empty-box'>
              <p>No boxes Found</p>
            </div>
          }
          {!hasMoreBoxes && displayedBoxesList.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '20px',
              color: '#999',
              fontSize: '14px'
            }}>
              <span>All boxes loaded ({displayedBoxesList.length} total)</span>
            </div>
          )}
        </div>
      </Spin>

      {
        editBox &&
        <EditBoxDimensionsModal
          setEditBox={setEditBox}
          editBox={editBox}
          boxesList={filteredBoxesList}
          boxIndex={boxIndex}
        />
      }

      {
        showBoxItems &&
        <BoxDetailsInfoDrawer
          setDefaultTab={setDefaultTab}
          showBoxItems={showBoxItems}
          setShowBoxItems={setShowBoxItems}
          boxIndex={boxIndex}
          boxesList={filteredBoxesList}
        />
      }

      <Modal
        saveText="Delete"
        onClose={() => setIsDeleteBox(false)}
        onSave={handleDeleteBox}
        closeText="Cancel" centerAlign size="small" open={isDeleteBox} removeIcon
        buttonDisabled={boxDeletingLoading}
      >
        <Spin tip='Loading...' spinning={boxDeletingLoading}>
          <DeleteModalStyleWrapper>
            <div className='delete-modal-wrapper'>
              <img src={WarningIcon} alt='WarningIcon' title='WarningIcon' />
              <Typography fontSize={24} component="h3" marginTop={3}>Warning</Typography>
              <p>Are you sure you want to delete this box?</p>
            </div>
          </DeleteModalStyleWrapper>
        </Spin>
      </Modal>

      <Modal
        saveText="Empty"
        onClose={() => setIsEmptyBox(false)}
        onSave={handleEmptyBox}
        closeText="Cancel" centerAlign size="small" open={isEmptyBox} removeIcon
        buttonDisabled={clearingBoxItems}
      >
        <Spin tip='Loading...' spinning={clearingBoxItems}>
          <DeleteModalStyleWrapper>
            <div className='delete-modal-wrapper'>
              <img src={WarningIcon} alt='WarningIcon' title='WarningIcon' />
              <Typography fontSize={24} component="h3" marginTop={3}>Warning</Typography>
              <p>Are you sure you want to clear this box?</p>
            </div>
          </DeleteModalStyleWrapper>
        </Spin>
      </Modal>

    </AccordionCardsDataWrapper>
  )
}

export default AccordionCardsData;
